/**
 * Formats a sequential order number as the XXXX-XXXX display form (E6-S1).
 * Used as a client-side fallback when the API payload predates the server-side
 * `orderNumberFormatted` field.
 */
export const formatOrderNumber = (orderNumber: number): string => {
    const digits = String(orderNumber).padStart(8, "0");
    return `${digits.slice(0, digits.length - 4)}-${digits.slice(-4)}`;
};

/**
 * Strips the dash from a formatted order number (e.g. "1000-0023" → "10000023")
 * before sending to the API, where SQL LIKE matches against the raw integer.
 * Strings with multiple dashes (tracking numbers) or non-numeric characters
 * (org names) are returned untouched.
 */
export const sanitizeOrderSearch = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) return "";
    // Match digits-dash-digits with exactly one dash — the formatted order
    // number pattern. Leave multi-dash tracking numbers unchanged.
    return /^\d+-\d+$/.test(trimmed) ? trimmed.replace("-", "") : trimmed;
};

/**
 * ADMIN: shown in place of a price when no live vendor offer prices the product
 * (`hasOffers === false`, P1): its 0 price means "no price". The storefront says
 * OUT_OF_STOCK instead (PO ruling 2026-11-02).
 */
export const PRICE_UNAVAILABLE = "Price unavailable";

/**
 * STOREFRONT: a product or variant the shopper can't order — no live vendor
 * offer (`hasOffers === false`, PO ruling 2026-11-02) or none in stock. One
 * copy for both, so the two read the same.
 */
export const OUT_OF_STOCK = "Out of stock";

/** Formats numbers as USD currency (e.g. `$1,234.56`). */
export const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
});

/**
 * Formats an ISO date string as a locale date (no time).
 * Returns "—" for null, empty, or invalid values.
 */
export const formatDate = (value: string | null): string => {
    if (!value) {
        return "—";
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString();
};

/**
 * Formats an ISO date string as a locale date and time; `options` go to
 * toLocaleString (e.g. `{ timeZoneName: "short" }` to name the zone).
 * Returns "—" for null, empty, or invalid values.
 */
export const formatDateTime = (
    value: string | null,
    options?: Intl.DateTimeFormatOptions,
): string => {
    if (!value) {
        return "—";
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime())
        ? "—"
        : date.toLocaleString(undefined, options);
};

interface UtcCalendarDateOptions {
    /** Days to move the date by, e.g. -1 for the last day of an exclusive end. */
    dayOffset?: number;
    /** "6/12/26" rather than "6/12/2026". */
    shortYear?: boolean;
}

/**
 * Formats an ISO date string's own calendar date as M/D/YYYY (or M/D/YY),
 * `dayOffset` days on. Read from the string, never through local time: a
 * boundary at midnight UTC would otherwise show as the day before everywhere
 * west of Greenwich. `Date.UTC` rolls day 0 back into the previous month (and
 * year), which keeps the offset month-length-correct.
 * Returns "—" for null, empty, or invalid values.
 */
export const formatUtcCalendarDate = (
    value: string | null,
    { dayOffset = 0, shortYear = false }: UtcCalendarDateOptions = {},
): string => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? "");
    if (!match) {
        return "—";
    }
    const [, year, month, day] = match;
    const date = new Date(
        Date.UTC(Number(year), Number(month) - 1, Number(day) + dayOffset),
    );
    const fullYear = String(date.getUTCFullYear());

    return `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${shortYear ? fullYear.slice(2) : fullYear}`;
};

export interface PiecePricing {
    /** Whether the per-piece breakdown should be shown alongside the unit price. */
    showPiece: boolean;
    /** Formatted unit/count price, e.g. "$12.97". */
    unitPrice: string;
    /** Formatted per-piece price (only meaningful when showPiece is true). */
    piecePrice: string;
    /** Effective pieces per unit (only meaningful when showPiece is true). */
    piecesPerUnit: number;
}

/**
 * Derives the wholesale unit/piece price breakdown for a product or variant.
 *
 * Single-piece products (`piecesPerUnit === 1`) and older payloads missing the
 * piece fields fall back to a single unit price (`showPiece: false`).
 */
export const formatPiecePricing = (
    price: number,
    piecesPerUnit?: number,
    pricePerPiece?: number,
): PiecePricing => {
    const showPiece =
        piecesPerUnit !== undefined &&
        pricePerPiece !== undefined &&
        piecesPerUnit > 1;

    return {
        showPiece,
        unitPrice: currencyFormatter.format(price),
        piecePrice: showPiece
            ? currencyFormatter.format(pricePerPiece as number)
            : "",
        piecesPerUnit: showPiece ? (piecesPerUnit as number) : 1,
    };
};

/**
 * The same breakdown with the piece price DERIVED: `price / piecesPerUnit`, under
 * formatPiecePricing's one rule for when a piece shows (more than one per unit). The cart
 * line prices a piece from its own vendor price this way, and the product page from the
 * price it shows — the API's pricePerPiece is that same division of the sku price
 * (the product's and VariantResponse's PricePerPiece).
 */
export const formatPiecePricingFromCount = (
    price: number,
    piecesPerUnit?: number,
): PiecePricing =>
    formatPiecePricing(
        price,
        piecesPerUnit,
        piecesPerUnit === undefined ? undefined : price / piecesPerUnit,
    );
